/**
 * Maps {@link OverallClassification} (and optional client fields) into drill-library
 * filter dimensions aligned with {@link MAIN_FILTER_CATEGORIES} in DrillLibrary.tsx.
 *
 * @see docs/features/260331-automated-driller-mvp/FEATURE_SPEC.md (G9, posture → drill filter contract)
 */

import type { OverallClassification } from './types';

/** Drill tag value for Phase category (e.g. tags['Phase']). */
export type DrillPhaseTag = 'Phase 1' | 'Phase 2' | 'Phase 3';

/** Posture taxonomy labels used by the Drill Library main filter (subset). */
export type DrillLibraryPostureLabel = 'Anterior Orientation' | 'Swayback';

export interface DrillSuggestionFilters {
  /** Same strings as SoP movement limitations (match drill tags['Movement Limitation']). */
  movementLimitations: string[];
  /** Mapped posture labels for drill tags['Posture'] / main filter. */
  postureLabels: DrillLibraryPostureLabel[];
  phaseTag: DrillPhaseTag;
  /** Optional biomechanical intent tag values (drill tags['Biomechanical Intent']). */
  biomechanicalIntents: string[];
  lateralized: 'Left' | 'Right' | null;
  grounded: 'Left' | 'Right' | null;
  /** Original classification phase code for persistence / debugging. */
  phaseCode: 'P1' | 'P2' | 'P3';
  /** Client suitability when provided: 0–3 lifting experience (untouched). */
  liftingExperience?: number;
  /**
   * Phase 6.3 — 0–10 clinical NRS (0 = pain-free). Callers MUST pass the
   * already-normalized value (`normalizePainLevelNrs` in
   * `src/lib/clients/roster-fields.ts`), never the raw `Client.painLevel` —
   * this field does no scale detection of its own.
   */
  painLevel?: number;
  isaPattern?: 'Narrow' | 'Wide' | null;
}

export function overallPhaseToDrillPhaseTag(phase: 'P1' | 'P2' | 'P3'): DrillPhaseTag {
  if (phase === 'P1') return 'Phase 1';
  if (phase === 'P2') return 'Phase 2';
  return 'Phase 3';
}

export function patternToDrillPostureLabels(pattern: 'APT' | 'SB' | 'N'): DrillLibraryPostureLabel[] {
  if (pattern === 'SB') return ['Swayback'];
  if (pattern === 'APT') return ['Anterior Orientation'];
  return [];
}

/**
 * MVP biomechanical intent hints from movement-limitation wording.
 * Extend with evaluation-rules-driven mapping when rules are wired (FEATURE_SPEC G9).
 */
export function inferBiomechanicalIntentsFromLimitations(limitations: string[]): string[] {
  const text = limitations.map((s) => s.toLowerCase()).join(' | ');
  const intents = new Set<string>();

  if (/\bir\b|internal rotation|external rotation/.test(text)) {
    intents.add('Occupancy');
  }
  if (/expan|rib cage|rib-cage|apical|bucket/.test(text)) {
    intents.add('Anterior Expansion');
    intents.add('Posterior Expansion');
  }
  if (/propuls|push-off|push off/.test(text)) {
    intents.add('Propulsion');
  }

  if (limitations.some(l => l.toLowerCase().includes('very limited lower body ir'))) {
    intents.add('Improve Pelvic & Femoral IR');
  }
  if (limitations.some(l => l.toLowerCase().includes('very limited lower body er'))) {
    intents.add('Improve Pelvic & Femoral ER');
  }

  return [...intents];
}

export function buildDrillSuggestionFilters(
  overall: OverallClassification,
  client?: { liftingExperience?: number; painLevel?: number }
): DrillSuggestionFilters {
  const biomechanicalIntents = inferBiomechanicalIntentsFromLimitations(overall.movementLimitations);

  return {
    movementLimitations: [...overall.movementLimitations],
    postureLabels: patternToDrillPostureLabels(overall.pattern),
    phaseTag: overallPhaseToDrillPhaseTag(overall.phase),
    biomechanicalIntents,
    lateralized: overall.lateralized,
    grounded: overall.grounded,
    phaseCode: overall.phase,
    liftingExperience: client?.liftingExperience,
    painLevel: client?.painLevel,
    isaPattern: overall.isaPattern ?? null,
  };
}
